import { Box, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Dialog from './Dialog';
import { useNavigate } from 'react-router-dom';
import { v2UIRoutePath } from '../../common/commons';

const useStyles = makeStyles(() => ({
    dialogTitle: {
        padding: '0px',
        '& .MuiButtonBase-root': {
            color: 'white',
            marginTop: '-10px'
        },
    },
    dialogheader: {
        minHeight: 28,
        padding: "10px",
        textAlign: 'left',
        background: "#7395d3",
        fontSize: '1rem',
        color: "white",

        '& span': {
            fontWeight: 'bold'
        },
        '& .modelheader': {
            paddingBottom: '5px',
            fontWeight: 'bold'
        }
    },
}));


const AllAlerts = {
    "closed_ticket_count": "Closed",
    "duplicate_ticket_count": "Duplicate",
    "pending_attachments_count": "Attachment Pending",
    "ingestion_failures": "Processing Queue",
    "review_ticket_count": "Review"
}
const AlertData = [{
    id: "1",
    name: "Attachment Pending",
    success: "/alerts/file_attachment.svg",
    error: "/alerts/file_attachment_error.svg",
    mapping: "pending_attachments_count",
    no_alert: "No pending attachments",
    alert: " requests pending attachments",
    routePath: "reports/pendingattachmentreport",
    additionalImageProps : {
        height : '50px'
    }
}, {
    id: "2",
    name: "Closed",
    success: "/alerts/closed_status.svg",
    error: "/alerts/closed_status_error.svg",
    mapping: "closed_ticket_count",
    no_alert: "No closed requests pending over an hour",
    alert: " closed requests pending over an hour",
    routePath: "reports/requestsagingreport",
    additionalRouteData : {
        ticket_state : "Closed"
    }
}, {
    id: "3",
    name: "Duplicate",
    success: "/alerts/duplicate_requests.svg",
    error: "/alerts/duplicate_requests_error.svg",
    mapping: "duplicate_ticket_count",
    no_alert: "No duplicate requests",
    alert: " duplicate requests",
    routePath: "reports/manualactionrequests"
}, {
    id: "4",
    name: "Processing Queue",
    success: "/alerts/data_ingest.svg",
    error: "/alerts/data_ingest_error.svg",
    mapping: ["ingestion_failures","review_failures"],
    no_alert: "No processing queue errors",
    alert: " requests errored in processing queue",
    routePath: "tracking/requestqueue",
    additionalRouteData : {
        status : "Failed"
    }
}, {
    id: "5",
    name: "Review",
    success: "/alerts/pending_review.svg",
    error: "/alerts/pending_review_error.svg",
    mapping: "review_ticket_count",
    no_alert: "No review requests",
    alert: " requests under manual review",
    routePath: "reports/manualactionrequests/reviewrequests",
    
}, {
    id: "6",
    name: "Cert Expiry",
    success: "/alerts/cert_expiry.svg",
    error: "/alerts/cert_expiry_error.svg",
    mapping: "cert_expiry",
    routePath: "resources/certmgmt"
}];

const CERT_ENVIRONMENTS = [
    { key: "prod", label: "Prod", tab: "PROD" },
    { key: "stage", label: "Stage", tab: "STAGE" }
];

const hasCertIssues = (envData) => (envData?.expired_count || 0) + (envData?.expiring_count || 0) > 0;

const hasCertExpiryAlert = (requestHealth) =>
    CERT_ENVIRONMENTS.some(({ key }) => hasCertIssues(requestHealth?.cert_expiry?.[key]));

const getCertExpiryMessage = (envData) => {
    if (!hasCertIssues(envData)) {
        return "No certs expiring in next 30 days";
    }
    const expired = envData.expired_count || 0;
    const expiring = envData.expiring_count || 0;
    let message = `${expired} certs expired, ${expiring} expiring within 30 days.`;
    if (expiring > 0 && envData.earliest_expiry_days !== null && envData.earliest_expiry_days !== undefined) {
        message += ` Earliest expires in ${envData.earliest_expiry_days} days`;
    }
    return message;
};

const linkButtonStyle = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: '#007bff',
    textDecoration: 'none',
    textAlign: 'left',
    width: '100%'
};


const AlertIcon = ({ requestHealth }) => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    
    const alerts = [];
    Object.keys(AllAlerts).forEach((_alert) => {
        if (requestHealth[_alert] && requestHealth[_alert] !== 0) {
            alerts.push(AllAlerts[_alert])
        }
    })
    if (hasCertExpiryAlert(requestHealth)) {
        alerts.push("Cert Expiry");
    }
    
    const classes = useStyles();
    const updateComponentState = () => {
        setIsOpen(false);
    }
    
    const comment = alerts.length === 0 
        ? "No downstream/upstream errors" 
        : `${alerts.length} alerts. ${alerts.join(", ")}`;
    
    return (
        <>
            <Tooltip title={comment} arrow>
                <IconButton
                    size="small"
                    onClick={() => { setIsOpen(true) }}
                    data-testid={"notificationButton"}
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.06)',
                            transform: 'translateY(-1px)',
                        },
                    }}
                >
                    <NotificationsActiveIcon data-testid={"notificationIcon"} sx={{
                        fontSize: '1.2rem',
                        color: alerts.length > 0 ? "#eb0a1e" : "#16a34a",
                    }} />
                </IconButton>
            </Tooltip>
            {isOpen && (<Dialog
                isOpen={isOpen}
                isLoading={false}
                alertMinimizedHeight = {true}
                updateDialogStatus={() => {
                    updateComponentState();
                }}
                popupGridData={AlertData}
                customRowHeight={60}
                dialogTitleClassName={classes.dialogTitle}
                popupColumns={[{
                    name: "Alert Status",
                    mapping: "rowId",
                    width : 150,
                    "align": "center",
                    renderCell: (params) => {
                        let imageUrl = params.row.success;
                        if (params.row.mapping === "cert_expiry") {
                            if (hasCertExpiryAlert(requestHealth)) {
                                imageUrl = params.row.error;
                            }
                        } else if(requestHealth && Array.isArray(params.row.mapping)){
                            let errorCount =0;
                            params.row.mapping.forEach((mappingName)=>{
                                errorCount = errorCount + requestHealth[mappingName]
                            })
                            if(errorCount > 0){
                                imageUrl = params.row.error;
                            }
                        } else if (requestHealth && requestHealth[params.row.mapping] > 0) {
                            imageUrl = params.row.error;
                        }

                        return <>
                            <img
                                data-testid={`${params.row.mapping}`}
                            alt=""
                            src={imageUrl} {...(params.row.additionalImageProps || { height : "30px"} )} ></img>
                        </>
                    }
                }, {
                    name: 'Alert Description',
                    mapping: 'activity',
                    renderCell: (params) => {
                        if (params.row.mapping === "cert_expiry") {
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', width: '100%' }}>
                                    {CERT_ENVIRONMENTS.map(({ key, label, tab }) => (
                                        <button
                                            key={key}
                                            data-testid={`cert_expiry_${key}`}
                                            onClick={() => {
                                                setIsOpen(false);
                                                navigate(`${v2UIRoutePath}${params.row.routePath}`, {
                                                    state: { environment: tab, fromredirection: Math.round(Math.random() * 1e5) }
                                                });
                                            }}
                                            style={linkButtonStyle}
                                        >
                                            <b>{label}:</b> {getCertExpiryMessage(requestHealth?.cert_expiry?.[key])}
                                        </button>
                                    ))}
                                </div>
                            );
                        }
                        let textToDisplay = params.row.no_alert;
                        if(requestHealth && Array.isArray(params.row.mapping)){
                            let errorCount =0;
                            params.row.mapping.forEach((mappingName)=>{
                                errorCount = errorCount + requestHealth[mappingName]
                            })
                            textToDisplay = `${errorCount} ${params.row.alert}`;
                        } else if (requestHealth && requestHealth[params.row.mapping] > 0) {
                            textToDisplay = `${requestHealth[params.row.mapping]} ${params.row.alert}`;
                        }

                        const handleClick = () => {
                            setIsOpen(false);
                            
                            if(params.row.name === "Processing Queue"){
                                const autoId = Math.round(Math.random() * 1e5);
                                navigate(`${v2UIRoutePath}${params.row.routePath}`, {
                                    state: {
                                        status: requestHealth.review_failures > 0 ? "Failed-Review" : "Failed-Ingestion",
                                        fromredirection: autoId
                                    }
                                });
                            } else {
                                navigate(`${v2UIRoutePath}${params.row.routePath}`, {
                                    state: {
                                        ...params.row?.additionalRouteData
                                    }
                                });
                            }
                        };

                        return (
                            <button 
                                onClick={handleClick}
                                style={linkButtonStyle}
                            >
                                {textToDisplay}
                            </button>
                        );
                    }
                }]}
                enablePagination={false}
                title={(
                    <Box paddingBottom={"5px"}  fontSize={"1rem"}>
                        <div className={classes.dialogheader}>Alerts</div>
                    </Box>
                )}
            />)}
        </>
    )
}
export default AlertIcon;